import React, { createContext, useState, useCallback, useContext, useEffect } from "react";

import { newArithmeticMeasure } from "@gooddata/sdk-model";

import * as Md from "../md/full";
import UserContext from "./User";
import FiltersContext from "./Filters";
import { getCurrencyLabel } from "../utils/currencyUtils";

const kpiLists = [
    {
        id: 1,
        name: "Adj. Rental",
        index: 23,
        roundValue: (value) => Math.round(Number(value)),
    },
    {
        id: 2,
        name: "Web Rental",
        index: 3,
        roundValue: (value) => Math.round(Number(value)),
    },
    {
        id: 3,
        name: "List Price",
        index: 1,
        roundValue: (value) => (value !== 0 ? Math.round(Number(value)) : null),
    },
    {
        id: 4,
        name: "Leasing Factor",
        index: 24,
        roundValue: (value) => (Number((100 * Number(value)).toFixed(2)) > 0 ? Number((100 * Number(value)).toFixed(2)) : null),
    },
];

const MeasuresContext = createContext(null);

const countriesMap = (code) => {
    switch (code) {
        case "BE":
            return "Belgium";
        case "DE":
            return "Germany";
        case "ES":
            return "Spain";
        case "FR":
            return "France";
        case "FF":
            return "France";
        case "IT":
            return "Italy";
        case "NL":
            return "Netherlands";
        case "UK":
            return "United Kingdom";
        case "GB":
            return "United Kingdom";
        case "AT":
            return "Austria";
        case "CH":
            return "Switzerland";
        case "NO":
            return "Norway";
        case "PL":
            return "Poland";
        case "PT":
            return "Portugal";

        default:
            return "UNKNOWN";
    }
};

const flagServiceMap = {
    flag_ser: Md.Service.Avg,
    flag_tyr: Md.Tyres.Avg,
    flag_bre: Md.Breakdown.Avg,
    flag_rep: Md.ReplacementVehicle.Avg,
    flag_ins: Md.Insurance.Avg,
    flag_fin: Md.Financialloss.Avg,
    flag_win: Md.Wintertyres.Avg,
    flag_co2: Md.Co2malus.Avg,
    flag_evb: Md.Evbonus.Avg,
    flag_insuranced: Md.InsuranceD.Avg,
    flag_insurancetpl: Md.InsuranceTPL.Avg,
};

// Bind the flag with measures, the flag measure and the associated index in the measures array
const flagServiceM = {
    flag_ser: { measure: Md.Service.Avg, flagMeasure: Md.FlService.Avg, measureIndex: 4, flIndex: 5 },
    flag_tyr: { measure: Md.Tyres.Avg, flagMeasure: Md.FlTyres.Avg, measureIndex: 6, flIndex: 7 },
    flag_bre: { measure: Md.Breakdown.Avg, flagMeasure: Md.FlBreakdown.Avg, measureIndex: 8, flIndex: 9 },
    flag_rep: { measure: Md.ReplacementVehicle.Avg, flagMeasure: Md.FlReplacement.Avg, measureIndex: 10, flIndex: 11 },
    flag_ins: { measure: Md.Insurance.Avg, flagMeasure: Md.FlInsurance.Avg, measureIndex: 12, flIndex: 13 },
    flag_fin: { measure: Md.Financialloss.Avg, flagMeasure: Md.FlFinancialloss.Avg, measureIndex: 14, flIndex: 15 },
    flag_win: { measure: Md.Wintertyres.Avg, flagMeasure: Md.FlWintertyres.Avg, measureIndex: 16, flIndex: 17 },
    flag_evb: { measure: Md.Evbonus.Avg, flagMeasure: Md.FlEvbonus.Avg, measureIndex: 18, flIndex: 19 },
    flag_co2: { measure: Md.Co2malus.Avg, flagMeasure: Md.FlCo2malus.Avg, measureIndex: 20, flIndex: 21 },
    flag_insuranced: { measure: Md.InsuranceD.Avg, flagMeasure: Md.FlInsuranceD.Avg, measureIndex: 34, flIndex: 35 },
    flag_insurancetpl: { measure: Md.InsuranceTPL.Avg, flagMeasure: Md.FlInsuranceTPL.Avg, measureIndex: 36, flIndex: 37 },
};

const Deposit = {
    "Total Payable by month": Md.AdjDeposit.Avg,
    0: Md.AdjDeposit0.Avg,
    1000: Md.AdjDeposit1000.Avg,
    2000: Md.AdjDeposit2000.Avg,
    2300: Md.AdjDeposit2300.Avg,
    2500: Md.AdjDeposit2500.Avg,
    2600: Md.AdjDeposit2600.Avg,
    2900: Md.AdjDeposit2900.Avg,
    3000: Md.AdjDeposit3000.Avg,
    4000: Md.AdjDeposit4000.Avg,
    4200: Md.AdjDeposit4200.Avg,
    4500: Md.AdjDeposit4500.Avg,
    4600: Md.AdjDeposit4600.Avg,
    4800: Md.AdjDeposit4800.Avg,
    5000: Md.AdjDeposit5000.Avg,
};

const MeasuresProvider = ({ children }) => {
    const { kpiSettings, IncServices } = useContext(UserContext);
    const { loading, getFirstFilterValue } = useContext(FiltersContext);
    const [selectedAdjDeposit, setSelectedAdjDeposit] = useState("Total Payable by month");
    const country = getFirstFilterValue("filterCountryId");
    const pathname = window.location.pathname;

    const [measures, setMeasures] = useState({
        seriesBy: [
            Md.FinRental.Avg, // 0
            Md.ListPrice.Avg, // 1
            Deposit[selectedAdjDeposit], // 2
            Md.Rental.Avg, // 3
            // Flags and Amounts
            Md.Service.Avg, // 4
            Md.FlService.Avg, // 5
            Md.Tyres.Avg, // 6
            Md.FlTyres.Avg, // 7
            Md.Breakdown.Avg, // 8
            Md.FlBreakdown.Avg, // 9
            Md.ReplacementVehicle.Avg, // 10
            Md.FlReplacement.Avg, // 11
            Md.Insurance.Avg, // 12
            Md.FlInsurance.Avg, // 13
            Md.Financialloss.Avg, // 14
            Md.FlFinancialloss.Avg, // 15
            Md.Wintertyres.Avg, // 16
            Md.FlWintertyres.Avg, // 17
            Md.Evbonus.Avg, // 18
            Md.FlEvbonus.Avg, // 19
            Md.Co2malus.Avg, // 20
            Md.FlCo2malus.Avg, // 21

            // User in filters
            Md.Ps.Avg, // 22

            // Complex metrics
            newArithmeticMeasure([Md.FinRental.Avg, Deposit[selectedAdjDeposit], Md.Service.Avg, Md.Tyres.Avg, Md.Breakdown.Avg, Md.ReplacementVehicle.Avg, Md.Insurance.Avg, Md.Financialloss.Avg, Md.Wintertyres.Avg, Md.Co2malus.Avg, Md.Evbonus.Avg], "sum"), // 23
            newArithmeticMeasure(
                [newArithmeticMeasure([Md.FinRental.Avg, Deposit[selectedAdjDeposit], Md.Service.Avg, Md.Tyres.Avg, Md.Breakdown.Avg, Md.ReplacementVehicle.Avg, Md.Insurance.Avg, Md.Financialloss.Avg, Md.Wintertyres.Avg, Md.Co2malus.Avg, Md.Evbonus.Avg], "sum"), Md.ListPrice.Avg],
                "ratio",
            ), // 24
            Md.Rv.Avg, // 25
            Md.Discount.Avg, // 26
            Md.Evolpct.Avg, // 27
            Md.VersionStdCount, // 28
            Md.Kw.Avg, // 29
            Md.VersionCount, // 30
            Md.Latestm.Avg, // 31
            Md.Latestw.Avg, // 32
            Md.OfferVolume, // 33

            // Newly added variables
            Md.InsuranceD.Avg, // 34
            Md.FlInsuranceD.Avg, // 35
            Md.InsuranceTPL.Avg, // 36
            Md.FlInsuranceTPL.Avg, // 37
        ],
        availableKpi: kpiLists,
        selectedKpi: kpiLists[0],
        updateMeasures: null,
        getScopeForTitle: null,
        getScopeForTitleHistory: null,
    });

    useEffect(() => {
        if (!loading) {
            if (kpiSettings && Object.keys(kpiSettings).length > 0) {
                const options = Object.keys(kpiSettings)
                    .filter((f) => kpiSettings[f] === 1)
                    .map((m) => flagServiceMap[m]);
                setMeasures((prevState) => ({
                    ...prevState,
                    seriesBy: [
                        Md.FinRental.Avg, // 0
                        Md.ListPrice.Avg, // 1
                        Deposit[selectedAdjDeposit], // 2
                        Md.Rental.Avg, // 3
                        Md.Service.Avg, // 4
                        Md.FlService.Avg, // 5
                        Md.Tyres.Avg, // 6
                        Md.FlTyres.Avg, // 7
                        Md.Breakdown.Avg, // 8
                        Md.FlBreakdown.Avg, // 9
                        Md.ReplacementVehicle.Avg, // 10
                        Md.FlReplacement.Avg, // 11
                        Md.Insurance.Avg, // 12
                        Md.FlInsurance.Avg, // 13
                        Md.Financialloss.Avg, // 14
                        Md.FlFinancialloss.Avg, // 15
                        Md.Wintertyres.Avg, // 16
                        Md.FlWintertyres.Avg, // 17
                        Md.Evbonus.Avg, // 18
                        Md.FlEvbonus.Avg, // 19
                        Md.Co2malus.Avg, // 20
                        Md.FlCo2malus.Avg, // 21
                        Md.Ps.Avg, // 22
                        IncServices ? newArithmeticMeasure([Md.FinRental.Avg, Deposit[selectedAdjDeposit], ...options], "sum") : newArithmeticMeasure([Md.Rental.Avg, Deposit[selectedAdjDeposit]], "sum"), // 23
                        IncServices
                            ? newArithmeticMeasure([newArithmeticMeasure([Md.FinRental.Avg, Deposit[selectedAdjDeposit], ...options], "sum"), Md.ListPrice.Avg], "ratio")
                            : newArithmeticMeasure([newArithmeticMeasure([Md.Rental.Avg, Deposit[selectedAdjDeposit]], "sum"), Md.ListPrice.Avg], "ratio"), // 24
                        Md.Rv.Avg, // 25
                        Md.Discount.Avg, // 26
                        Md.Evolpct.Avg, // 27
                        Md.VersionStdCount, // 28
                        Md.Kw.Avg, // 29
                        Md.VersionCount, // 30
                        Md.Latestm.Avg, // 31
                        Md.Latestw.Avg, // 32
                        Md.OfferVolume, // 33

                        // Newly added variables
                        Md.InsuranceD.Avg, // 34
                        Md.FlInsuranceD.Avg, // 35
                        Md.InsuranceTPL.Avg, // 36
                        Md.FlInsuranceTPL.Avg, // 37
                    ],
                }));
            }
        }
    }, [loading, kpiSettings, selectedAdjDeposit, IncServices]);

    useEffect(() => {
        if (country !== "GB") setSelectedAdjDeposit("Total Payable by month");
    }, [country]);

    const getScopeForTitle = useCallback(
        (kpiTitle) => {
            const countryCode = getFirstFilterValue("filterCountryId");
            const country = countriesMap(countryCode);
            const duration = getFirstFilterValue("filterDuration");
            const channel1 = getFirstFilterValue("filterChannel1");
            const monthCode = getFirstFilterValue("filterMthCode");
            const kpiName = kpiTitle || measures?.selectedKpi?.name;
            const adjdeposit = countryCode === "GB" && pathname !== "/trends" ? `| Target deposit : ${selectedAdjDeposit === "Total Payable by month" ? "default" : selectedAdjDeposit} ` : "";

            return `${country} | ${duration} | ${channel1} | ${monthCode} ${adjdeposit} | ${kpiName} ${kpiName === "Leasing Factor" ? "" : getCurrencyLabel(countryCode)}`;
        },
        [measures, getFirstFilterValue, selectedAdjDeposit],
    );

    const getScopeForTitleHistory = useCallback(
        (page = null) => {
            const countryCode = getFirstFilterValue("filterCountryId");
            const country = countriesMap(countryCode);
            const duration = getFirstFilterValue("filterDuration");
            const channel1 = getFirstFilterValue("filterChannel1");
            const kpiName = page ? page : measures?.selectedKpi?.name;
            const currency = getCurrencyLabel(countryCode);
            const adjdeposit = countryCode === "GB" && pathname !== "/trends" ? `| Target deposit : ${selectedAdjDeposit === "Total Payable by month" ? "default" : selectedAdjDeposit} ` : "";

            return `${country} | ${duration} | ${channel1} ${adjdeposit} | ${kpiName} ${currency}`;
        },
        [measures, getFirstFilterValue, selectedAdjDeposit],
    );

    const updateMeasures = useCallback((name, value) => setMeasures((prevState) => ({ ...prevState, [name]: value })), []);

    return <MeasuresContext.Provider value={{ ...measures, updateMeasures, getScopeForTitle, getScopeForTitleHistory, selectedAdjDeposit, setSelectedAdjDeposit }}>{children}</MeasuresContext.Provider>;
};

export default MeasuresContext;

export { MeasuresProvider, countriesMap, flagServiceMap, Deposit, flagServiceM };
